import React, { useEffect, useState } from 'react';
import { X, Check, Send, AlertCircle, User, Phone, Clock, CalendarHeart } from 'lucide-react';
import { Button } from './ui/Button';
const WEB3FORMS_KEY = '00888808-a120-4a58-b457-d0746e22e1c7';
const SHEETDB_URL = 'https://sheetdb.io/api/v1/q3yik1ir2kdyj';
const MAX_BOOKINGS_PER_SITTING = 80;
const inputClass = 'w-full h-11 px-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans text-sm';
const labelClass = 'block text-sm font-medium text-[#1A1A1A] mb-1.5';
const required = <span className="text-red-500 ml-0.5" data-id="element-19">*</span>;
export function BrunchBooking() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sittingCounts, setSittingCounts] = useState<{
    '11:00': number;
    '13:30': number;
    '16:00': number;
  }>({
    '11:00': 80,
    '13:30': 0,
    '16:00': 0
  });
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistData, setWaitlistData] = useState({
    name: '',
    phone: '',
    guests: '',
    preferredSitting: ''
  });
  const [isSubmittingWaitlist, setIsSubmittingWaitlist] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    guests: '',
    sitting: '',
    allergies: [] as string[],
    otherAllergy: ''
  });
  const sumGuests = (rows: any[]) => rows.reduce((sum, row) => sum + (parseInt(row['Antal personer'], 10) || 0), 0);
  const fetchSittingCounts = async () => {
    setLoadingCounts(true);
    try {
      const [res1100, res1330, res1600] = await Promise.all([fetch(`${SHEETDB_URL}/search?Sittning%2011%3A00=Ja`, {
        headers: {
          Accept: 'application/json'
        }
      }), fetch(`${SHEETDB_URL}/search?Sittning%2013%3A30=Ja`, {
        headers: {
          Accept: 'application/json'
        }
      }), fetch(`${SHEETDB_URL}/search?Sittning%2016%3A00=Ja`, {
        headers: {
          Accept: 'application/json'
        }
      })]);
      const data1100 = await res1100.json();
      const data1330 = await res1330.json();
      const data1600 = await res1600.json();
      setSittingCounts({
        '11:00': Array.isArray(data1100) ? sumGuests(data1100) : 0,
        '13:30': Array.isArray(data1330) ? sumGuests(data1330) : 0,
        '16:00': Array.isArray(data1600) ? sumGuests(data1600) : 0
      });
    } catch {
      setSittingCounts({
        '11:00': 0,
        '13:30': 0,
        '16:00': 0
      });
    } finally {
      setLoadingCounts(false);
    }
  };
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      fetchSittingCounts();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        allergies: checked ? [...prev.allergies, value] : prev.allergies.filter(a => a !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    const allergyList = [...formData.allergies, ...(formData.otherAllergy ? [`Annat: ${formData.otherAllergy}`] : [])];
    const payload = {
      access_key: WEB3FORMS_KEY,
      to: 'info@topnotchbakery.se',
      subject: `Bokning Brunch Party: ${formData.name} (${formData.guests} pers)`,
      from_name: formData.name,
      Namn: formData.name,
      Telefon: formData.phone,
      'Antal gäster': formData.guests,
      Sittning: formData.sitting,
      Allergier: allergyList.length > 0 ? allergyList.join(', ') : 'Inga',
      Typ: 'Bordsbokning Brunch Party'
    };
    try {
      const sheetPayload = {
        data: {
          Namn: formData.name,
          Telefonnummer: formData.phone,
          'Antal personer': String(formData.guests),
          'Sittning 11:00': formData.sitting.includes('11:00') ? 'Ja' : 'Nej',
          'Sittning 13:30': formData.sitting.includes('13:30') ? 'Ja' : 'Nej',
          'Sittning 16:00': formData.sitting.includes('16:00') ? 'Ja' : 'Nej'
        }
      };
      const [emailRes] = await Promise.all([fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }), fetch(SHEETDB_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sheetPayload)
      }).catch(() => {})]);
      const emailData = await emailRes.json();
      if (emailData.success) {
        setIsSuccess(true);
      } else {
        setSubmitError('Något gick fel. Försök igen eller kontakta oss direkt.');
      }
    } catch {
      setSubmitError('Kunde inte skicka bokningen. Kontrollera din internetanslutning och försök igen.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleWaitlistSubmit = async () => {
    if (!waitlistData.name || !waitlistData.phone || !waitlistData.guests || !waitlistData.preferredSitting) {
      setWaitlistError('Fyll i alla fält.');
      return;
    }
    setIsSubmittingWaitlist(true);
    setWaitlistError('');
    try {
      const sheetPayload = {
        data: {
          Namn: waitlistData.name,
          Telefonnummer: waitlistData.phone,
          'Antal personer': waitlistData.guests || '1',
          'Sittning 11:00': waitlistData.preferredSitting === '11:00 - 13:00' ? 'Väntelista' : '',
          'Sittning 13:30': waitlistData.preferredSitting === '13:30 - 15:30' ? 'Väntelista' : '',
          'Sittning 16:00': waitlistData.preferredSitting === '16:00 - 18:00' ? 'Väntelista' : ''
        }
      };
      const res = await fetch(SHEETDB_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sheetPayload)
      });
      if (res.ok) {
        setWaitlistSuccess(true);
      } else {
        setWaitlistError('Något gick fel. Försök igen.');
      }
    } catch {
      setWaitlistError('Kunde inte skicka. Kontrollera din internetanslutning.');
    } finally {
      setIsSubmittingWaitlist(false);
    }
  };
  const handleClose = () => {
    setIsOpen(false);
    // Reset form after closing
    setTimeout(() => {
      setIsSuccess(false);
      setWaitlistSuccess(false);
      setShowWaitlist(false);
      setWaitlistData({
        name: '',
        phone: '',
        guests: '',
        preferredSitting: ''
      });
      setWaitlistError('');
      setFormData({
        name: '',
        phone: '',
        guests: '',
        sitting: '',
        allergies: [],
        otherAllergy: ''
      });
      setSubmitError('');
    }, 300);
  };
  return <>
      <section className="w-full px-4 flex flex-col items-center animate-in slide-in-from-bottom-8 duration-1000 delay-100 fill-mode-backwards" data-id="element-20">
        <div className="max-w-4xl w-full bg-transparent rounded-2xl overflow-hidden relative" data-id="element-21">
          <div className="p-8 md:p-12 flex flex-col items-center gap-8" data-id="element-22">
            {/* Rubrik & intro – före bilden */}
            <div className="text-center space-y-4 max-w-lg" data-id="element-23">
              <p className="text-[#C5A55A] font-semibold text-sm uppercase tracking-widest" data-id="element-24">
                🎉 Top Notch fyller halvt decennium! 🎉
              </p>
              <p className="text-[#1A1A1A]/80 leading-relaxed text-[15px]" data-id="element-25">
                5 år av smak, gemenskap och oförglömliga stunder – tack vare er!
                Nu firar vi med något helt nytt som aldrig gjorts i Växjö. Gör
                er redo! 🔥
              </p>
            </div>

            {/* Bild */}
            <img src="https://cdn.magicpatterns.com/uploads/3HW4252PpaMh8rocb7DZQ1/Red_Minimalist_Brunch_Club_Logo.png" alt="Top Notch Brunch Party" className="w-full max-w-xs mx-auto mix-blend-multiply" data-id="element-26" />

            {/* Vad ni kan förvänta er */}
            <div className="w-full text-center space-y-5" data-id="element-27">
              <p className="text-sm font-semibold text-[#1A1A1A]/70 uppercase tracking-wider" data-id="element-28">
                Ni kan förvänta er
              </p>
              <div className="flex flex-wrap justify-center gap-3" data-id="element-29">
                {[{
                emoji: '🎧',
                text: 'DJ & feel-good vibes'
              }, {
                emoji: '🍳',
                text: 'Brett utbud'
              }, {
                emoji: '🍹',
                text: 'Blend Bar'
              }, {
                emoji: '🧁',
                text: 'Bakverk & bakelser'
              }].map(item => <span key={item.text} className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFF8F0] border border-[#C5A55A]/15 rounded-full text-sm text-[#1A1A1A]/80" data-id="element-30">
                    <span data-id="element-31">{item.emoji}</span>
                    {item.text}
                  </span>)}
              </div>
            </div>

            {/* Boka bord-knapp */}
            <div className="pt-2" data-id="element-32">
              <Button size="lg" className="w-full md:w-auto text-lg px-10" onClick={() => setIsOpen(true)} data-id="element-33">
                Boka bord nu
              </Button>
            </div>

            {/* Datum, tid & sittningar */}
            <div className="w-full flex flex-col sm:flex-row gap-4" data-id="element-34">
              <div className="flex-1 bg-white/80 p-5 rounded-xl border border-[#C5A55A]/15 flex items-start gap-4" data-id="element-35">
                <div className="bg-white p-2 rounded-lg shadow-sm border border-[#C5A55A]/10 shrink-0" data-id="element-36">
                  <CalendarHeart className="w-6 h-6 text-[#C5A55A]" data-id="element-37" />
                </div>
                <div data-id="element-38">
                  <h4 className="font-serif text-lg text-[#1A1A1A] mb-1" data-id="element-39">
                    28 mars
                  </h4>
                  <p className="text-[#1A1A1A]/60 text-sm" data-id="element-40">
                    Klostergatan 7, Växjö
                  </p>
                </div>
              </div>

              <div className="flex-1 bg-white/80 p-5 rounded-xl border border-[#C5A55A]/15 flex items-start gap-4" data-id="element-41">
                <div className="bg-white p-2 rounded-lg shadow-sm border border-[#C5A55A]/10 shrink-0" data-id="element-42">
                  <Clock className="w-6 h-6 text-[#C5A55A]" data-id="element-43" />
                </div>
                <div data-id="element-44">
                  <h4 className="font-serif text-lg text-[#1A1A1A] mb-1" data-id="element-45">
                    Sittning 1
                  </h4>
                  <p className="text-[#1A1A1A]/60 text-sm" data-id="element-46">11:00 - 13:00</p>
                </div>
              </div>

              <div className="flex-1 bg-white/80 p-5 rounded-xl border border-[#C5A55A]/15 flex items-start gap-4" data-id="element-47">
                <div className="bg-white p-2 rounded-lg shadow-sm border border-[#C5A55A]/10 shrink-0" data-id="element-48">
                  <Clock className="w-6 h-6 text-[#C5A55A]" data-id="element-49" />
                </div>
                <div data-id="element-50">
                  <h4 className="font-serif text-lg text-[#1A1A1A] mb-1" data-id="element-51">
                    Sittning 2
                  </h4>
                  <p className="text-[#1A1A1A]/60 text-sm" data-id="element-52">13:30 - 15:30</p>
                </div>
              </div>

              <div className="flex-1 bg-white/80 p-5 rounded-xl border border-[#C5A55A]/15 flex items-start gap-4" data-id="element-53">
                <div className="bg-white p-2 rounded-lg shadow-sm border border-[#C5A55A]/10 shrink-0" data-id="element-54">
                  <Clock className="w-6 h-6 text-[#C5A55A]" data-id="element-55" />
                </div>
                <div data-id="element-56">
                  <h4 className="font-serif text-lg text-[#1A1A1A] mb-1" data-id="element-57">
                    Sittning 3
                  </h4>
                  <p className="text-[#1A1A1A]/60 text-sm" data-id="element-58">16:00 - 18:00</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Booking Modal */}
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={e => e.target === e.currentTarget && handleClose()} data-id="element-59">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300" data-id="element-60">
            {isSuccess ? <div className="p-10 text-center" data-id="element-61">
                <div className="w-20 h-20 bg-[#C5A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-62">
                  <Check className="w-10 h-10 text-[#C5A55A]" data-id="element-63" />
                </div>
                <h3 className="text-3xl font-serif text-[#1A1A1A] mb-3" data-id="element-64">
                  Tack för din bokning!
                </h3>
                <p className="text-[#1A1A1A]/70 mb-8 leading-relaxed text-lg" data-id="element-65">
                  Vi har tagit emot din bokning till Top Notch Brunch Party. Vi
                  ser fram emot att träffa dig!
                </p>
                <Button onClick={handleClose} className="w-full" size="lg" data-id="element-66">
                  Stäng fönstret
                </Button>
              </div> : <>
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b border-[#C5A55A]/10 bg-white rounded-t-2xl shrink-0" data-id="element-67">
                  <div className="flex items-start gap-4" data-id="element-68">
                    <div className="text-3xl bg-[#FFF8F0] w-14 h-14 flex items-center justify-center rounded-xl border border-[#C5A55A]/20 shrink-0" data-id="element-69">
                      🥂
                    </div>
                    <div data-id="element-70">
                      <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-71">
                        Boka bord
                      </h3>
                      <p className="text-[#C5A55A] font-medium text-sm uppercase tracking-wide" data-id="element-72">
                        Top Notch Brunch Party
                      </p>
                    </div>
                  </div>
                  <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-73">
                    <X className="w-6 h-6" data-id="element-74" />
                  </button>
                </div>

                {/* Form Body */}
                <div className="overflow-y-auto flex-1 p-6 md:p-8" data-id="element-75">
                  <div className="mb-8 p-4 bg-[#FFF8F0] rounded-xl border border-[#C5A55A]/20 space-y-4" data-id="element-76">
                    <div className="space-y-2" data-id="element-77">
                      <p className="text-sm font-semibold text-[#1A1A1A]" data-id="element-78">
                        Priser
                      </p>
                      <div className="flex flex-col gap-1" data-id="element-79">
                        <p className="text-sm text-[#1A1A1A]/80" data-id="element-80">
                          <strong data-id="element-81">Förbokning:</strong> 359 kr/person
                        </p>
                        <p className="text-sm text-[#1A1A1A]/80" data-id="element-82">
                          <strong data-id="element-83">Drop-in:</strong> 439 kr/person
                        </p>
                      </div>
                      <p className="text-xs text-[#1A1A1A]/60 leading-relaxed" data-id="element-84">
                        I priset ingår en brunch buffé med ett urval av massa
                        lagat och bakat.
                      </p>
                    </div>
                    <div className="border-t border-[#C5A55A]/15 pt-3" data-id="element-85">
                      <p className="text-xs text-[#1A1A1A]/60 leading-relaxed" data-id="element-86">
                        🥗 Det kommer att finnas vegetariska, glutenfria och
                        laktosfria alternativ.
                      </p>
                    </div>
                  </div>

                  <form id="booking-form" onSubmit={handleSubmit} className="space-y-6" data-id="element-87">
                    <div data-id="element-88">
                      <label className={labelClass} data-id="element-89">Namn {required}</label>
                      <div className="relative" data-id="element-90">
                        <input type="text" name="name" required value={formData.name} onChange={handleChange} className={`${inputClass} pl-10`} placeholder="För- och efternamn" data-id="element-91" />
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-92" />
                      </div>
                    </div>

                    <div data-id="element-93">
                      <label className={labelClass} data-id="element-94">
                        Telefonnummer {required}
                      </label>
                      <div className="relative" data-id="element-95">
                        <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={`${inputClass} pl-10`} placeholder="070-123 45 67" data-id="element-96" />
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-97" />
                      </div>
                    </div>

                    <div data-id="element-98">
                      <label className={labelClass} data-id="element-99">
                        Antal personer {required}
                      </label>
                      <div className="relative" data-id="element-100">
                        <input type="number" name="guests" required min={1} max={20} value={formData.guests} onChange={handleChange} className={inputClass} placeholder="Antal personer" data-id="element-101" />
                      </div>
                    </div>

                    <div data-id="element-102">
                      <label className={labelClass} data-id="element-103">
                        Välj sittning {required}
                      </label>
                      {(() => {
                  const sittings = [{
                    value: '11:00 - 13:00',
                    key: '11:00' as const
                  }, {
                    value: '13:30 - 15:30',
                    key: '13:30' as const
                  }, {
                    value: '16:00 - 18:00',
                    key: '16:00' as const
                  }];
                  const allFull = sittings.every(s => sittingCounts[s.key] >= MAX_BOOKINGS_PER_SITTING);
                  const someFull = sittings.some(s => sittingCounts[s.key] >= MAX_BOOKINGS_PER_SITTING);
                  const available = sittings.filter(s => sittingCounts[s.key] < MAX_BOOKINGS_PER_SITTING);
                  return <div className="space-y-3" data-id="element-104">
                            {allFull ? <div className="flex items-start gap-3 p-4 bg-[#FFF8F0] border border-[#C5A55A]/20 rounded-xl text-sm text-[#1A1A1A]/80" data-id="element-105">
                                <AlertCircle className="w-5 h-5 text-[#C5A55A] shrink-0 mt-0.5" data-id="element-106" />
                                <div data-id="element-107">
                                  <p className="font-semibold text-[#1A1A1A] mb-1" data-id="element-108">
                                    Alla sittningar är fullbokade
                                  </p>
                                  <p data-id="element-109">
                                    Du kan skriva upp dig på väntelistan nedan
                                    så kontaktar vi dig om en plats blir ledig.
                                  </p>
                                </div>
                              </div> : <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-110">
                                {sittings.map(option => {
                        const isFull = sittingCounts[option.key] >= MAX_BOOKINGS_PER_SITTING;
                        return <label key={option.value} className={`flex items-center gap-3 p-4 border rounded-xl transition-colors ${isFull ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' : 'border-[#C5A55A]/20 cursor-pointer hover:bg-[#FFF8F0] has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A] has-[:checked]:ring-1 has-[:checked]:ring-[#C5A55A]'}`} data-id="element-111">
                                      <input type="radio" name="sitting" value={option.value} required={!allFull} disabled={isFull} checked={formData.sitting === option.value} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-112" />
                                      <div className="flex flex-col" data-id="element-113">
                                        <span className="text-sm font-bold text-[#1A1A1A]" data-id="element-114">
                                          {option.value}
                                        </span>
                                        {isFull ? <span className="text-xs text-red-500 font-semibold mt-0.5" data-id="element-115">
                                            Fullbokat (
                                            {sittingCounts[option.key]}/
                                            {MAX_BOOKINGS_PER_SITTING})
                                          </span> : <span className="text-xs text-[#1A1A1A]/50 mt-0.5" data-id="element-116">
                                            {sittingCounts[option.key]}/
                                            {MAX_BOOKINGS_PER_SITTING} bokade
                                          </span>}
                                      </div>
                                    </label>;
                      })}
                              </div>}

                            {someFull && !allFull && <div className="flex items-start gap-3 p-3 bg-[#FFF8F0] border border-[#C5A55A]/15 rounded-xl text-sm" data-id="element-117">
                                <AlertCircle className="w-4 h-4 text-[#C5A55A] shrink-0 mt-0.5" data-id="element-118" />
                                <div className="text-[#1A1A1A]/70" data-id="element-119">
                                  <p data-id="element-120">
                                    Vissa sittningar är fullbokade. Vi
                                    rekommenderar{' '}
                                    <span className="font-semibold text-[#1A1A1A]" data-id="element-121">
                                      {available.map(s => s.value).join(' eller ')}
                                    </span>
                                    .{' '}
                                    <button type="button" onClick={() => setShowWaitlist(!showWaitlist)} className="underline text-[#C5A55A] font-semibold hover:text-[#b8943f]" data-id="element-122">
                                      {showWaitlist ? 'Dölj väntelista' : 'Ställ dig i kö för fullbokad sittning'}
                                    </button>
                                  </p>
                                </div>
                              </div>}

                            {(allFull || showWaitlist) && <>
                                {waitlistSuccess ? <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700" data-id="element-123">
                                    <Check className="w-5 h-5 shrink-0 mt-0.5" data-id="element-124" />
                                    <p data-id="element-125">
                                      Tack! Du är nu uppskriven på väntelistan.
                                      Vi hör av oss om en plats blir ledig.
                                    </p>
                                  </div> : <div className="space-y-3 p-4 bg-white border border-[#C5A55A]/15 rounded-xl" data-id="element-126">
                                    <p className="text-sm font-semibold text-[#1A1A1A]" data-id="element-127">
                                      📋 Väntelista
                                    </p>
                                    <input type="text" placeholder="Namn" required value={waitlistData.name} onChange={e => setWaitlistData(prev => ({
                          ...prev,
                          name: e.target.value
                        }))} className={inputClass} data-id="element-128" />
                                    <input type="tel" placeholder="Telefonnummer" required value={waitlistData.phone} onChange={e => setWaitlistData(prev => ({
                          ...prev,
                          phone: e.target.value
                        }))} className={inputClass} data-id="element-129" />
                                    <input type="number" placeholder="Antal personer" required min={1} max={20} value={waitlistData.guests} onChange={e => setWaitlistData(prev => ({
                          ...prev,
                          guests: e.target.value
                        }))} className={inputClass} data-id="element-130" />
                                    <div className="space-y-2" data-id="element-131">
                                      <p className="text-xs text-[#1A1A1A]/60" data-id="element-132">
                                        Vilken sittning vill du stå i kö för?
                                      </p>
                                      <div className="flex gap-2" data-id="element-133">
                                        {sittings.filter(s => sittingCounts[s.key] >= MAX_BOOKINGS_PER_SITTING).map(s => <label key={s.value} className={`flex-1 text-center py-2 px-3 text-sm border rounded-lg cursor-pointer transition-colors ${waitlistData.preferredSitting === s.value ? 'bg-[#FFF8F0] border-[#C5A55A] text-[#1A1A1A] font-semibold' : 'border-[#C5A55A]/20 text-[#1A1A1A]/60 hover:bg-[#FFF8F0]/50'}`} data-id="element-134">
                                              <input type="radio" name="waitlist-sitting" value={s.value} required checked={waitlistData.preferredSitting === s.value} onChange={e => setWaitlistData(prev => ({
                                ...prev,
                                preferredSitting: e.target.value
                              }))} className="sr-only" data-id="element-135" />
                                              {s.value}
                                            </label>)}
                                      </div>
                                    </div>
                                    {waitlistError && <p className="text-xs text-red-600" data-id="element-136">
                                        {waitlistError}
                                      </p>}
                                    <Button type="button" onClick={handleWaitlistSubmit} isLoading={isSubmittingWaitlist} className="w-full" data-id="element-137">
                                      Skriv upp mig på väntelistan
                                    </Button>
                                    <p className="text-xs text-[#1A1A1A]/50 leading-relaxed mt-2" data-id="element-138">
                                      Vi hör av oss om en plats blir ledig för
                                      den sittning du önskat. Observera att det
                                      är högst osannolikt att en plats frigörs –
                                      vi rekommenderar i första hand att boka en
                                      av de andra sittningarna som fortfarande
                                      har lediga platser.
                                    </p>
                                  </div>}
                              </>}
                          </div>;
                })()}
                    </div>

                    <div className="flex gap-2 items-start pt-2" data-id="element-139">
                      <AlertCircle className="w-4 h-4 text-[#C5A55A] shrink-0 mt-0.5" data-id="element-140" />
                      <p className="text-xs text-[#1A1A1A]/50 leading-relaxed" data-id="element-141">
                        Man kan komma in som senast 30 min från sittningens
                        starttid. Därefter släpps bordet.
                      </p>
                    </div>

                    {submitError && <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-142">
                        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-143" />
                        <p data-id="element-144">{submitError}</p>
                      </div>}
                  </form>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-[#C5A55A]/10 bg-white rounded-b-2xl shrink-0 flex justify-between items-center gap-4" data-id="element-145">
                  <Button variant="ghost" onClick={handleClose} type="button" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-id="element-146">
                    Avbryt
                  </Button>
                  <Button type="submit" form="booking-form" isLoading={isSubmitting} className="min-w-[180px]" data-id="element-147">
                    <Send className="w-4 h-4 mr-2" data-id="element-148" />
                    Boka bord
                  </Button>
                </div>
              </>}
          </div>
        </div>}
    </>;
}